// @flow

// Skipping story doc for this component since it is purpose to interact with environment
/* eslint-disable ta-code-conventions/has-matching-storydoc */

import * as React from 'react';

import { ApolloProvider } from 'react-apollo';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { removeDirectivesFromDocument } from 'apollo-utilities';
import { Hermes } from 'apollo-cache-hermes';
import logger from '@ta/common.logging';
import { start, end } from '@ta/platform.performance';
import { featureIsEnabled } from '@ta/common.features';
import DummyCache from './DummyCache';

const GRAPHQL_ENDPOINT = '/data/graphql/batched';

const typesKnownToHaveIds = new Set();
const typesMissingIds = new Set();
const typesThatHaveBeenWarnedAbout = new Set();

/** The type of the Apollo cache */
export type ApolloCache = any;

/** The type of the query result */
export type ApolloQueryResult = any;

type Props = {|
  children: React.Node,
  client: any,
|};

/** Returns the Apollo client based on type of rendering */
// eslint-disable-next-line no-restricted-globals
export const getApolloClient = () => (SERVER ? global : window).__APOLLO_CLIENT__;

/**
 * Allows writing data to the dummy cache. Useful if you have a nested query
 * that is satisfied by an outer query, but without normalization you can't
 * fulfill it during SSR.
 */
export const writeToDummyCache = (query: any, variables: { [string]: any }, result: any) => {
  const { cache } = getApolloClient();
  if (!cache.isDummy) {
    return; // no-op
  }
  cache.write({ query, variables, result });
};

/**
 * Add SSR-formatted graphql results to the cache.  Input is same format as PageManifest.apolloCache.
 * This can be used to incorporate results from SSR requests that happen after page load
 */
export const restoreGraphQLResults = (apolloCache: any) => {
  if (!apolloCache) {
    return;
  }
  const { cache } = getApolloClient();
  if (cache.isDummy) {
    cache.restore(apolloCache);
    return;
  }
  // cache is a hermes cache -- we need to populate via a new DummyCache, since it does some
  // transformations on the entries
  const dummyCache = new DummyCache();
  dummyCache.restore(apolloCache);
  dummyCache.getEntries().forEach(entry => cache.write(entry));
};

/** Returns the name of the field to use to identify an object. */
export function getObjectIdField(typename: string): string {
  switch (typename) {
    case 'LinkPost':
      return 'postId';
    case 'Repost':
      return 'repostId';
    case 'MediaBatch':
      return 'mediaBatchId';
    case 'LocationInformation':
    case 'CruiseInfoCruiseSearchInfo':
    case 'TopDestShelfInfo':
    case 'ListingUpdateResponse':
    case 'VotesForLocationResponse':
    case 'RentalCarsLeadOffer':
      return 'locationId';
    case 'AttractionProductInformation':
      return 'attractionProductId';
    default:
      return 'id';
  }
}

/** Extracts an object's identifier */
export function getObjectId(obj: { [string]: any }): any {
  if (!obj || !obj.__typename) return null;
  let id = obj[getObjectIdField(((obj.__typename: any): string))];

  // Special case until we take care of WP-586
  if (!id && obj.__typename === 'MemberProfile' && obj.userId) {
    id = obj.userId;
  }
  if (!id && obj.__typename === 'Repost' && obj.repostId) {
    id = obj.repostId;
  }

  if (
    id &&
    obj.__typename === 'FeedSectionItem' &&
    obj.suggestedFolloweesInfo &&
    obj.suggestedFolloweesInfo.suggestedFolloweeType
  ) {
    id = `${id}${obj.suggestedFolloweesInfo.suggestedFolloweeType}`;
  }

  return id;
}

/** Extracts a globally unique identifier for an object suitable for use as a cache key */
export function getCacheKey(obj: { [string]: any }): ?string {
  const id = getObjectId(obj);
  const finalId = id == null ? null : `${obj.__typename}:${id.toString()}`;
  if (DEBUG && obj.__typename) {
    if (finalId) {
      if (
        typesMissingIds.has(obj.__typename) &&
        !typesThatHaveBeenWarnedAbout.has(obj.__typename)
      ) {
        typesThatHaveBeenWarnedAbout.add(obj.__typename);
        logger.warn(`Type ${obj.__typename} was missing ID earlier but now has one`);
      }
      typesKnownToHaveIds.add(obj.__typename);
    } else {
      if (
        typesKnownToHaveIds.has(obj.__typename) &&
        !typesThatHaveBeenWarnedAbout.has(obj.__typename)
      ) {
        typesThatHaveBeenWarnedAbout.add(obj.__typename);
        logger.warn(`Type ${obj.__typename} is missing ID now but had one earlier`);
      }
      typesMissingIds.add(obj.__typename);
    }
  }
  return finalId;
}

/** Function to build a new Apollo cache */
export function buildCache(options?: { [string]: any }): ApolloCache {
  return new Hermes({
    verbose: false,
    freeze: DEBUG,
    addTypename: true,
    entityIdForNode: getCacheKey,
    ...options,
  });
}

/**
 * Update the global service timings object.
 */
const recordServiceTimings = serviceTimings => {
  // eslint-disable-next-line no-restricted-globals
  const ctx = SERVER ? global : window;
  if (!ctx.__SERVICE_TIMINGS__) {
    ctx.__SERVICE_TIMINGS__ = {};
  }
  const aggregatedTimings = ctx.__SERVICE_TIMINGS__;

  Object.entries(serviceTimings).forEach(([serviceCall, stats]) => {
    if (!aggregatedTimings[serviceCall]) {
      aggregatedTimings[serviceCall] = {};
    }
    Object.entries((stats: any)).forEach(([metricName, metricValue]) => {
      if (!aggregatedTimings[serviceCall][metricName]) {
        aggregatedTimings[serviceCall][metricName] = 0;
      }
      aggregatedTimings[serviceCall][metricName] += ((metricValue: any): number);
    });
  });
};

const recordErrors = errors => {
  // eslint-disable-next-line no-restricted-globals
  const ctx = SERVER ? global : window;
  if (!ctx.__GRAPHQL_ERRORS__) {
    ctx.__GRAPHQL_ERRORS__ = [];
  }
  errors.forEach(error =>
    ctx.__GRAPHQL_ERRORS__.push(`${new Date().toLocaleString()}: ${error.message}`),
  );
};

const stripStaticDirectiveLink = new ApolloLink((operation, forward) => {
  // eslint-disable-next-line no-param-reassign
  operation.query = removeDirectivesFromDocument([{ name: 'static' }], operation.query);
  return forward(operation);
});

const doReplaceCache = () => {
  start('Apollo Cache Replacement');

  const client = getApolloClient();
  const oldCache = client.cache;
  const newCache = buildCache();

  // Load existing data from the old cache into the new cache
  oldCache.getEntries().forEach(entry => newCache.write(entry));

  // Copy the watchers over from the old cache to the new cache
  oldCache.getWatchers().forEach(watcher => newCache.watch(watcher));

  // Finally, flip the switch to activate the new cache
  client.cache = newCache;
  client.store.cache = newCache;

  end('Apollo Cache Replacement');
};

/** Replace Apollo cache */
export const replaceCache = (now: ?boolean) => {
  if (featureIsEnabled('dummy_cache_on_client')) {
    return;
  }
  const { cache } = getApolloClient();
  if (!cache.isDummy) {
    return;
  }
  if (!now) {
    setTimeout(() => replaceCache(true), 0);
    return;
  }
  doReplaceCache();
};

const ensureDummyCacheIsReplacedLink = new ApolloLink((operation, forward) => {
  replaceCache(false);
  return forward(operation).map(data => {
    replaceCache(true);
    return data;
  });
});

const stashDebugInfoLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    if (response) {
      if (response.extensions && response.extensions.serviceTimings) {
        recordServiceTimings(response.extensions.serviceTimings);
      }
      if (response.errors) {
        recordErrors(response.errors);
      }
    }
    return response;
  }),
);

const getLink = (csrfToken: ?string, customFetcher) => {
  if (!SERVER) {
    const httpLink = new BatchHttpLink({
      uri: `${GRAPHQL_ENDPOINT}${DEBUG && !csrfToken ? '?csrfBypass=true' : ''}`,
      credentials: 'same-origin',
      headers: csrfToken && {
        'X-Requested-By': csrfToken,
      },
      // eslint-disable-next-line no-restricted-globals
      fetch: customFetcher || window.fetch,
      batchMax: 50,
      batchInterval: 100,
    });

    if (!DEBUG) {
      return httpLink;
    }

    return stashDebugInfoLink.concat(httpLink);
  }

  // eslint-disable-next-line global-require
  const microserviceBatchLink = require('./MicroserviceBatchLink').default;
  if (!global.__DEBUG_TOOL_ENABLED__) {
    return microserviceBatchLink;
  }
  return stashDebugInfoLink.concat(microserviceBatchLink);
};

/** Function to build a new Apollo client */
export function buildClient(cache: ?ApolloCache, csrfToken: ?string, customFetcher: ?any) {
  let link = stripStaticDirectiveLink.concat(getLink(csrfToken, customFetcher));

  if (!SERVER) {
    link = ensureDummyCacheIsReplacedLink.concat(link);
  }

  return new ApolloClient({
    ssrMode: SERVER,
    link,
    cache: cache || new DummyCache(),
  });
}

function Apollo({ children, client }: Props) {
  return <ApolloProvider client={client || buildClient()}>{children}</ApolloProvider>;
}
Apollo.defaultProps = { client: null };

export default Apollo;
